import React, { useEffect, useState, Fragment, act } from "react";
import { Card, Button, Spinner, Badge } from "react-bootstrap";
import DataTable from "../../components/table";
import { useNotification } from "../../components/notification";
import { useConfirmation } from "../../contexts/confirmationContext";
import apiService from "../../services/apiService";
import { formatTime } from "../../utils";
import { useAuth } from "../../contexts/authContext";

const OrderCancelRequests = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { showConfirmation } = useConfirmation();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [page, setPage] = useState({ PageNumber: 1, PageSize: 15 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isRefreshData, setIsRefreshData] = useState(false);

  useEffect(() => {
    const fetchRequests = async () => {
      setIsLoading(true);
      try {
        const apiURL =
          user.role === "super_admin" || user.role === "administrator"
            ? `/orders-cancel-request/all`
            : `/orders-cancel-request/user`;

        const res = await apiService.get(apiURL, {
          page: page.PageNumber,
          limit: page.PageSize,
        });
        console.log(res);
        setRequests(res.orderCancelRequests || []);
        setTotalRecords(res.pagination.total);
      } catch (err) {
        showNotification("danger", err?.message || "Failed to load requests.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchRequests();
  }, [
    user.role,
    page.PageNumber,
    page.PageSize,
    showNotification,
    isRefreshData,
  ]);

  const handleAction = (id, actionType) => {
    let description = "";
    let reasonInput = "";

    if (actionType === "approve") {
      description =
        "Are you sure you want to approve this cancellation request?";
    } else if (actionType === "reject") {
      description = (
        <div>
          <p>Are you sure you want to reject this cancellation request?</p>
          <label htmlFor="reject-reason" className="form-label mt-3">
            Reason for rejection
          </label>
          <textarea
            id="reject-reason"
            className="form-control"
            rows="3"
            onChange={(e) => {
              reasonInput = e.target.value;
            }}
            placeholder="Enter your reason here..."
          ></textarea>
        </div>
      );
    }

    showConfirmation({
      title: `${actionType === "approve" ? "Approve" : "Reject"} Request`,
      description,
      confirmText: `Yes, ${actionType}`,
      cancelText: "Cancel",
      variant: "primary",
      onConfirm: async () => {
        if (actionType === "reject" && !reasonInput.trim()) {
          showNotification("danger", "Please provide a reason for rejection.");
          return;
        }

        setLoadingId(id);
        try {
          await apiService.post(`/orders-cancel-request/${id}/${actionType}`, {
            actionRemarks: reasonInput,
          });
          setIsRefreshData(!isRefreshData);
          showNotification("success", `Request ${actionType}d successfully`);
        } catch (err) {
          showNotification(
            "danger",
            typeof err === "string"
              ? err
              : err?.message || `Failed to ${actionType} request`
          );
        } finally {
          setLoadingId(null);
        }
      },
    });
  };

  const columns = [
    {
      accessorKey: "order.orderNumber",
      header: "Order #",
      cell: (info) => <>{info.row.original.Order?.orderNumber}</>,
    },
    {
      accessorKey: "User",
      header: "Requested By",
      cell: (info) => {
        const { role, firstName, lastName } = info.row.original.User || {};
        return (
          <Badge bg="info">
            {role?.toUpperCase()} - {firstName} {lastName}
          </Badge>
        );
      },
    },
    {
      accessorKey: "reason",
      header: "Reason",
      cell: (info) => <>{info.getValue()}</>,
    },
    {
      accessorKey: "createdAt",
      header: "Requested On",
      cell: (info) => <>{formatTime(info.getValue())}</>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => <>{info.getValue()}</>,
    },
    {
      accessorKey: "actionRemarks",
      header: "Action Remarks",
      cell: (info) => {
        const remarks = info.getValue();
        if (!remarks) return <span className="text-muted">-</span>;

        return (
          <div
            className="action-remarks-cell"
            style={{
              maxWidth: "300px",
              wordWrap: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.4",
            }}
            title={remarks} // Shows full text on hover
          >
            {remarks.length > 100 ? `${remarks.substring(0, 100)}...` : remarks}
          </div>
        );
      },
      size: 300, // Set fixed width for this column
    },
  ];

  if (user.role === "super_admin") {
    columns.push({
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => {
        const { id, status } = info.row.original;
        return (
          status == "pending" && (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                size="sm"
                variant="success"
                disabled={loadingId === id}
                onClick={() => handleAction(id, "approve")}
              >
                {loadingId === id ? <Spinner size="sm" /> : "Approve"}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={loadingId === id}
                onClick={() => handleAction(id, "reject")}
              >
                {loadingId === id ? <Spinner size="sm" /> : "Reject"}
              </Button>
            </div>
          )
        );
      },
    });
  }

  const handlePageChange = (pageNumber, pageSize) => {
    setPage({ PageNumber: pageNumber, PageSize: pageSize });
  };

  const noDataMessage =
    user.role === "super_admin"
      ? "No cancellation requests available. Currently, no customers have requested to cancel any orders. Once a request is submitted, it will appear here for your review."
      : "Your cancellation requests will appear here. You can track all the orders for which you’ve requested cancellation. Once submitted, a super admin will review and take action.";

  return (
    <Card className="p-4 rounded-4">
      <h5 className="mb-4">Order Cancellation Requests</h5>
      <DataTable
        columns={columns}
        data={requests}
        loadingState={isLoading}
        totalRecords={totalRecords}
        page={page}
        onPageChange={handlePageChange}
        searchPlaceholder="Search by Order Number or User..."
        noDataMessageTitle="No Cancellation Requests Found"
        noDataMessage={noDataMessage}
      />
    </Card>
  );
};

export default OrderCancelRequests;
